import { useLazyQuery, useMutation } from '@apollo/client';
import Breadcrumbs from 'components/Common/Breadcrumb';
import Loader from 'components/Loader';
import MiniCards from 'components/MiniCard/mini-card';
import { DELETE_POST_COMMENT } from 'containers/community/graphql/community.mutation';
import { COMMUNITY_POST_GET_BY_ID, GET_COMMUNITY_COMMENTS_LIST } from 'containers/community/graphql/community.query';
import useRequest from 'hooks/useRequest';
import { useRouter } from 'hooks/useRouter';
import { isEmpty, map } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody, CardTitle, Col, Container, Row } from 'reactstrap';
import confirm from 'reactstrap-confirm';
import { buildURL, createPostDetailsLink, showImage } from 'utils/helper';
import { SMKLinks } from 'utils/links';
import AddCommentModal from './addCommentModal';
import Comment from './comment';
import UpdateCommentModal from './updateCommentModal';

const UpdatePostComments = () => {
  const router = useRouter();
  const postId = router.query?.id;
  const commentSize = 10;

  const [comments, setComments] = useState([]);
  const [commentsPagination, setCommentsPagination] = useState({});
  const [commentCounts, setCommentCounts] = useState({});
  const [nestedCommentsMap, setNestedCommentsMap] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedComment, setSelectedComment] = useState('');
  const [updatedText, setUpdatedText] = useState('');

  const [postDetails, setPostDetails] = useState(null);
  // const [schoolDetails, setSchoolDetails] = useState(null);

  const [isOpenCommentModal, setIsOpenCommentModal] = useState(false);
  const [selectedReplyComment, setSelectedReplyComment] = useState(null);

  const togggleCommentModal = () => {
    setIsOpenCommentModal(!isOpenCommentModal);
    setSelectedReplyComment(null);
  };

  // const [getSchoolDetailsRequest, { loading: getSchoolDetailsLoading }] = useRequest({
  //   method: 'GET',
  //   onSuccess: (data) => {
  //     setSchoolDetails(data);
  //   },
  // });

  const [getPostDetailsRequest, { loading: getPostDetailsLoading }] = useLazyQuery(COMMUNITY_POST_GET_BY_ID, {
    fetchPolicy: 'no-cache',
    onCompleted: async (data) => {
      const payload = data?.admin_community_post_getById;
      setPostDetails(payload);
      // await getSchoolDetailsRequest({
      //   url: `/school/admin/by-feed-id/${payload?.id}`,
      // });
    },
  });

  const [getNestedCommentsList, { loading: nestedCommentsLoading }] = useLazyQuery(GET_COMMUNITY_COMMENTS_LIST, {
    fetchPolicy: 'no-cache',
  });

  const [getCommentsList, { loading: commentsLoading }] = useLazyQuery(GET_COMMUNITY_COMMENTS_LIST, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setIsLoadingMore(false);
      const payload = data?.admin_community_post_comment_search;
      if (payload && !isEmpty(payload?.results)) {
        setComments((prev) => [...prev, ...(payload?.results || [])]);
        setCommentsPagination(payload?.pagination);
        setCommentCounts(payload?.commentCounts);

        const nestedCommentsGrouped = payload?.comments?.reduce((acc, comment) => {
          acc[comment.commentId] = [...(acc[comment.commentId] || []), comment];
          return acc;
        }, {});

        setNestedCommentsMap((prev) => ({ ...prev, ...nestedCommentsGrouped }));
      }
    },
    onError: (err) => {
      setIsLoadingMore(false);
      console.warn('Error fetching comments', err);
    },
  });

  const [deleteUserCommentRequest, { loading: deleteUserCommentLoading }] = useMutation(DELETE_POST_COMMENT, {
    onCompleted: () => {
      setIsLoading(true);
      window.location.reload();
    },
    onError: (err) => {
      console.error('Error:', err);
    },
  });

  const fetchComments = async (filters) => {
    await getCommentsList({
      variables: {
        searchDTO: {
          page: 1,
          size: commentSize,
          sortBy: 'createdDate',
          sortOrder: 1,
          postId,
          ...filters,
        },
      },
    });
  };

  const handleLoadMoreComments = () => {
    setIsLoadingMore(true);
    setCurrentPage((prevPage) => {
      const nextPage = prevPage + 1;
      fetchComments({
        page: nextPage,
        size: commentSize,
      });
      return nextPage;
    });
  };

  const handleLoadMoreNestedComments = async (commentId, page) => {
    const existingComments = nestedCommentsMap[commentId] || [];
    const nextPage = page ? page : Math.floor(existingComments.length / commentSize) + 1;

    const response = await getNestedCommentsList({
      variables: {
        searchDTO: {
          page: nextPage,
          postId,
          size: commentSize,
          sortBy: 'createdDate',
          sortOrder: 1,
          commentId,
        },
      },
    });

    const payload = response?.data?.admin_community_post_comment_search;
    if (!isEmpty(payload)) {
      const nestedComments = payload?.results || [];
      const mergedComments = [...existingComments, ...nestedComments]?.filter(
        (value, index, self) => index === self.findIndex((t) => t.id === value.id)
      );

      const updatedCounts = commentCounts.some((item) => item.comment_id === commentId)
        ? commentCounts.map((item) =>
            item.comment_id === commentId ? { ...item, count: payload?.pagination?.totalCount } : item
          )
        : [...commentCounts, { comment_id: commentId, count: payload?.pagination?.totalCount }];

      setCommentCounts(updatedCounts);

      const updatedComments = {
        ...nestedCommentsMap,
        [commentId]: mergedComments,
      };
      setNestedCommentsMap(updatedComments);
    }
  };

  const toggleModal = (data) => {
    if (!isEmpty(data)) {
      setSelectedComment(data);
    } else {
      setSelectedComment(null);
    }
  };

  const handleDeleteComment = async (data, removeChildren = false) => {
    const result = await confirm({
      title: 'Remove comment',
      message: 'Are you sure, you want to remove this comment?',
      confirmText: 'Confirm',
      confirmColor: 'primary',
      cancelColor: 'link text-danger',
    });
    if (result) {
      toggleModal();
      await deleteUserCommentRequest({
        variables: {
          commentId: data?.id,
          removeChildren,
        },
      });
    }
  };

  useEffect(() => {
    fetchComments();
  }, []);

  useEffect(() => {
    if (postId) {
      getPostDetailsRequest({
        variables: {
          postId,
        },
      });
    }
  }, [postId]);

  const miniCards = [
    {
      title: 'Comment count',
      text: `${postDetails?.stats?.commentCount || ''}`,
    },
    {
      title: 'Reaction count',
      text: `${postDetails?.stats?.reactionCount || ''}`,
    },
    {
      title: 'isAnonymous',
      text: `${postDetails?.isAnonymous ? 'Yes' : 'No'} `,
      textColor: `${postDetails?.isAnonymous ? 'text-success' : 'text-danger'}`,
    },
  ];

  console.warn('selectedReplyComment', selectedReplyComment);

  return (
    <div className="page-content">
      <Loader
        isActive={
          deleteUserCommentLoading || nestedCommentsLoading || commentsLoading || isLoading || getPostDetailsLoading
        }
      />
      <Breadcrumbs title="Community Post Details" breadcrumbItem="Community Post Details" />

      <Container fluid>
        <Row>
          <Col xl="6">
            <Card className="overflow-hidden">
              <CardBody className="p-3">
                <Row className="align-items-center">
                  <Col sm="2">
                    <div className="avatar-md profile-user-wid m-auto d-flex align-items-center justify-content-center">
                      <i className="bx bxs-message-square-detail" style={{ fontSize: '50px', color: '#3460BB' }} />
                    </div>
                  </Col>
                  <Col sm="9">
                    <h5 className="font-size-15 text-truncate mb-1">{postDetails?.title}</h5>
                    <p className="text-muted mb-0 text-truncate">{postDetails?.slug}</p>
                  </Col>
                  <Col sm="1">
                    <Link
                      to={buildURL(SMKLinks.COMMUNITY_GROUP_POSTS_EDIT, {
                        id: postDetails?.id,
                        groupId: postDetails?.group?.id,
                      })}>
                      <i className="fas fa-pencil-alt text-success fs-5 ms-2" />
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="6">
            <Row>
              {map(miniCards, (card, key) => (
                <MiniCards
                  mdCol={4}
                  title={card?.title}
                  text={card?.text}
                  textColor={card?.textColor}
                  iconClass={card?.iconClass}
                  key={`_card_${key}`}
                  removeIcon={true}
                />
              ))}
            </Row>
          </Col>

          <Col xl="12" clas>
            <Card className="mb-5">
              <CardTitle className="p-3 border-bottom">Post Details</CardTitle>
              <CardBody className="p-3">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <p className="mb-1">Group Name:</p>
                    <h5 className="mb-0">
                      <Link
                        target="_blank"
                        to={buildURL(SMKLinks.COMMUNITY_GROUPS_DETAILS, {
                          id: postDetails?.group?.id,
                        })}>
                        {postDetails?.group?.name}
                        <i className="bx bx-link-external fs-4 ms-2" />
                      </Link>
                    </h5>
                  </div>
                  {/* {!isEmpty(schoolDetails) && (
                    <div className="col-md-12 mb-3">
                      <p className="mb-1">School Name:</p>
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={process.env.REACT_APP_SCHOOLMYKIDS_WEB_URL + `/school/${schoolDetails?.slug}`}
                        className="h5 mb-0 d-flex align-items-center text-primary">
                        {schoolDetails?.name}
                        <i className="bx bx-link-external fs-4 ms-2" />
                      </a>
                    </div>
                  )} */}
                  <div className="col-md-12 mb-3">
                    <p className="mb-1">Post Title:</p>
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={createPostDetailsLink(postDetails)}
                      className="h5 mb-0 d-flex align-items-center text-primary">
                      {postDetails?.title}
                      <i className="bx bx-link-external fs-4 ms-2" />
                    </a>
                  </div>

                  <div className="col-md-12 mt-4">
                    <h6 className="mb-1">Content:</h6>
                    <p>{postDetails?.content}</p>
                  </div>

                  {!isEmpty(postDetails?.media) && (
                    <div className="col-md-4 mt-4">
                      <h6 className="mb-3">Images:</h6>
                      <div className="d-flex align-items-center">
                        {postDetails?.media?.map((file) => {
                          if (file?.type?.includes('image')) {
                            return (
                              <img
                                src={showImage(file?.original)}
                                className="mb-4 me-4"
                                style={{ height: '150px', objectFit: 'contain' }}
                              />
                            );
                          } else if (file?.type?.includes('video')) {
                            return <video controls src={showImage(file?.original)} />;
                          } else {
                            return <div className="bg-light p-2 rounded-lg w-100">Unsupported format</div>;
                          }
                        })}
                      </div>
                    </div>
                  )}

                  {!isEmpty(postDetails?.poll) && (
                    <div className="col-md-6 mt-4">
                      <h6 className="mb-3">Poll Details:</h6>
                      <div className="mb-3 border rounded-2 p-3">
                        <h5 className="card-title">{postDetails.poll.title}</h5>
                        <p className="text-muted mb-2">
                          Allow Mutiple answers: <b>{postDetails.poll.allowMultiple ? 'True' : 'False'}</b>
                        </p>
                        <p className="text-muted mb-2">
                          Ends on: {new Date(postDetails.poll.endDate).toLocaleDateString()}
                        </p>

                        <ul className="list-group">
                          {postDetails?.poll?.options?.map((option, index) => (
                            <li key={index} className="list-group-item">
                              {option}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
            <div className="mt-4">
              <div className="mb-4 d-flex gap-3 flex-column flex-md-row align-items-center justify-content-between">
                <h4 className="mb-0">Comments List</h4>

                <Button size="md" color="success" className="ms-3 rounded-pill px-3" onClick={togggleCommentModal}>
                  <i className="mdi mdi-plus me-1" />
                  Add Comment
                </Button>
              </div>

              {!isEmpty(comments) ? (
                comments.map((comment) => (
                  <Comment
                    key={comment.id}
                    commentId={comment.id}
                    togggleCommentModal={togggleCommentModal}
                    setSelectedReplyComment={setSelectedReplyComment}
                    comment={comment}
                    toggleModal={toggleModal}
                    handleDeleteComment={handleDeleteComment}
                    loading={isLoading}
                    setUpdatedText={setUpdatedText}
                    nestedComments={nestedCommentsMap[comment.id] || []}
                    commentCounts={
                      !isEmpty(commentCounts) ? commentCounts?.find((e) => e?.comment_id === comment?.id) : null
                    }
                    onLoadMoreNested={(page) => {
                      handleLoadMoreNestedComments(comment?.id, page);
                    }}
                  />
                ))
              ) : (
                <Card>
                  <CardBody>
                    <div style={{ minHeight: 170 }} className="d-flex justify-content-center align-items-center">
                      <div className="text-center">
                        <i className="bx bxs-error" style={{ fontSize: '50px', color: '#ffbc42' }} />
                        <h5 className="mt-3 text-secondary">No Comment Found!</h5>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>

            {comments?.length < commentsPagination?.totalCount && (
              <div className="mt-3 mb-5">
                <button
                  type="button"
                  disabled={isLoadingMore}
                  onClick={handleLoadMoreComments}
                  className="btn btn-link p-0 load-more-link text-primary">
                  {commentsPagination?.totalCount - comments?.length == '1'
                    ? `${commentsPagination?.totalCount - comments?.length} previous reply`
                    : `${commentsPagination?.totalCount - comments?.length} previous replies`}
                </button>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <AddCommentModal
        isOpen={isOpenCommentModal}
        toggleModal={togggleCommentModal}
        postId={postId}
        selectedReplyComment={selectedReplyComment}
      />

      <UpdateCommentModal
        toggleModal={toggleModal}
        updatedText={updatedText}
        setUpdatedText={setUpdatedText}
        selectedComment={selectedComment}
        postId={postId}
      />
    </div>
  );
};

export default UpdatePostComments;
